/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Fri Apr 12 2024
 **/

import { PropsWithChildren, useEffect, useState } from "react";
import { version } from "mediasoup-client";
import { useLoaderData } from "react-router-dom";
import isEmpty from "lodash-es/isEmpty";
import DetectRTC from "detectrtc";

import { EventHandler } from "@rfice/eventhandler";

import { Maybe } from "@/frameworks/libraries/components";
import { Properties } from "@/frameworks/libraries/properties";
import { Environments, Utility, Configure } from "@/frameworks/libraries/utilities";
import { useAccountActions, useServiceInitialize } from "@/frameworks/core/hooks";
import { StorageProvider } from "@/frameworks/core/storage";
import { useBeforeUnload } from "@/frameworks/libraries/hooks";
import ServiceInitalizer from "@/frameworks/core/hooks/useServiceInitialize/private/ServiceInitalizer";
import { usePersistActions } from "@/frameworks/core/persistence";
import { SystemError } from "@/frameworks/core/commons";
import { UserOptionProvider } from "../permission";

const LOGTAG = "#MainServiceProvider#";

/*
	기능에 중시된 종합 컴포넌트입니다. UI 컴포넌트를 추가하지 마세요.
*/
export default function MainServiceProvider({ children }: PropsWithChildren) {
	const serviceInitialize = useServiceInitialize();
	const [isDataReady, setDataReady] = useState(false);
	const { logout } = useAccountActions();
	const { users } = usePersistActions();
	const loaderData = useLoaderData() as Users.People.Data;

	useEffect(() => {
		start_application();

		async function start_application() {
			console.groupCollapsed(LOGTAG, "Initializing...");
			console.info(
				"#Mode#",
				Properties.productMode,
				Properties.isSolution ? `Solution-${Properties.solution}` : "SaaS"
			);
			console.info("#User#", Configure.is_authenticated ? "Logged in" : "Guest");
			console.info("#unique_id#", Properties.browserTabID, Properties.finger_print);
			console.info("#API#", Properties.property.apiDomain);
			console.info("#Web#", Properties.property.webDomain);
			console.info("#Partner#", Utility.URLNormalize(Environments.env("PARTNER_DOMAIN")));
			console.info("#Location#", window.location.pathname);
			console.info("#Mediasoup# client", version);
			console.info("#BrowserInfo#", DetectRTC.browser, window.navigator.userAgent);
			if (navigator.storage && navigator.storage.estimate) {
				const estimate = await navigator.storage.estimate();
				console.info(
					"#Storage#",
					estimate.usage && (estimate.usage / 1024 / 1024 / 1024).toFixed(2),
					"of",
					estimate.quota && (estimate.quota / 1024 / 1024 / 1024).toFixed(2),
					"GB"
				);
			}
			console.groupEnd();

			if (!isEmpty(loaderData)) {
				users.set_user(Configure.userID, {
					...loaderData,
					id: loaderData.userId,
					// @ts-ignore
					email: loaderData.username,
					name: loaderData.name || ""
				});
				Configure.userID = loaderData.userId || "";
			}
			try {
				ServiceInitalizer.getInstance().activate();
			} catch (error) {
				if (error instanceof SystemError) {
					if (error.cause.errorCode === "1111111") {
						alert(error.message);
						logout();
						return;
					}
				}
				console.error(LOGTAG, "서비스 초기화 중 오류가 발생했습니다", error);
			}
			await serviceInitialize();
			setDataReady(true);
		}
	}, []);

	useEffect(() => {
		isDataReady && EventHandler.fire("$Service::Ready$");
	}, [isDataReady]);

	useBeforeUnload(() => {
		Properties.dispose();
	});

	return (
		<>
			<UserOptionProvider />
			<Maybe test={isDataReady}>
				<>{children}</>
				<StorageProvider />
			</Maybe>
		</>
	);
}
